import type { Permission } from '@ssn/shared';

export interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: string;
  /**
   * The permission that makes this destination worth showing. An array means
   * "any one is enough" — Leadership Requests is the case that needs it:
   * Department Leadership, developers and directors each hold a different
   * single `ticket:read:*` permission, and all three should see the link.
   */
  readonly permission: Permission | readonly Permission[];
  /** Match child routes as well, e.g. /tasks/1001 highlights "All Tasks". */
  readonly matchPrefix?: boolean;
  readonly group: 'work' | 'oversight' | 'admin';
}

/**
 * The whole navigation, as data (§21).
 *
 * A developer never sees a control they cannot use, because the list is filtered
 * by permission at render time. That is presentation only — every route behind
 * these links is independently authorised on the server, so removing the link is
 * never what makes the destination safe.
 */
export const NAVIGATION: readonly NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'LayoutDashboard', permission: 'platform:access', group: 'work' },
  { to: '/tasks/mine', label: 'My Tasks', icon: 'ListChecks', permission: 'task:read:own', group: 'work' },
  { to: '/tasks', label: 'All Tasks', icon: 'FolderKanban', permission: 'task:read:team', matchPrefix: true, group: 'work' },
  {
    to: '/tickets',
    label: 'Leadership Requests',
    icon: 'MessageSquareText',
    permission: ['ticket:read:own', 'ticket:read:team', 'ticket:read:all'],
    matchPrefix: true,
    group: 'work',
  },
  { to: '/review', label: 'Review Queue', icon: 'Gavel', permission: 'review:queue:read', matchPrefix: true, group: 'oversight' },
  { to: '/team', label: 'Team', icon: 'Users', permission: 'user:read', matchPrefix: true, group: 'oversight' },
  { to: '/analytics', label: 'Analytics', icon: 'BarChart3', permission: 'analytics:read:team', group: 'oversight' },
  { to: '/audit', label: 'Audit Log', icon: 'ScrollText', permission: 'audit:read', group: 'admin' },
  { to: '/settings', label: 'Settings', icon: 'Settings', permission: 'settings:read', group: 'admin' },
];

export const GROUP_LABELS: Record<NavItem['group'], string> = {
  work: 'Work',
  oversight: 'Oversight',
  admin: 'Administration',
};
